"use client";

import { ArrowRight, Spinner } from "@phosphor-icons/react/dist/ssr";
import { Slot } from "radix-ui";
import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "@/services/styles/cn";
import styles from "./button.module.css";

const loader = <Spinner className="animate-spin" />;

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  variant?: "primary" | "secondary" | "tertiary" | "destructive" | "ghost" | "link" | "transparent";
  size?: "default" | "sm" | "lg" | "icon";
  loading?: boolean;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  arrowOnHover?: boolean;
  asChild?: boolean;
}

export function Button({
  disabled = false,
  loading = false,
  asChild = false,
  variant = "primary",
  size = "default",
  arrowOnHover = variant === "primary" || variant === "secondary",
  children,
  className,
  leftSection = null,
  rightSection = null,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const isLoading = !disabled && loading;
  const Component = asChild && !isDisabled ? Slot.Root : "button";
  const customProps = Component === "button" ? ({ disabled: isDisabled, type: "button" } as const) : {};

  const isIconOnly = size === "icon";
  const leftElement = isLoading ? loader : leftSection;
  const rightElement = rightSection ?? (arrowOnHover ? <ArrowRight data-arrow /> : null);

  return (
    <Component
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(styles.button, className)}
      {...customProps}
      {...props}
    >
      {!isIconOnly && leftElement}
      <Slot.Slottable>{isIconOnly && isLoading ? loader : children}</Slot.Slottable>
      {!isIconOnly && rightElement}
    </Component>
  );
}
